import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Box, Typography } from '@mui/material';
import Files from './tabs/Files';
import InfoIcon from '@mui/icons-material/Info';
import About from './tabs/About';
import ApiIcon from '@mui/icons-material/Api';
import DatasetCatalog from './tabs/DatasetCatalog';
import DatasetCatalogIcon from '@mui/icons-material/FormatListBulleted';
import Inspector from './tabs/Inspector';
import InspectorIcon from '@mui/icons-material/FindInPage';
import FilesIcon from '@mui/icons-material/Inventory';
import JupyterLink from './tabs/JupyterLink';
import MyJobs from './tabs/MyJobs';
import WorkIcon from '@mui/icons-material/Work';
import DatasetSelect from './DatasetSelect';
import Admin from './tabs/Admin';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

import styles from './TabSelect.module.css';
import { Background } from '../lib/styled';
import { setValue } from '../state/tabSelectSlice';
import { useAdminVisibility } from '../lib/useAdminVisibility';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

const DEFAULT_TAB_PATH = 'DatasetCatalog';

function TabSelect() {
    const { activeTab } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    // Redux `value` is now a derived mirror of the URL — useful for legacy
    // consumers (DatasetSelect, anything reading state.tabSelect.value) but
    // never the source of truth. URL → `value`, not the other way around.
    const value = useSelector(state => state.tabSelect.value);
    const dispatch = useDispatch();

    const tabIconSize = 'large';

    // Argo "My Jobs" tab is gated on a runtime env flag so envs without
    // an argo-batch-pipeline install (e.g. local dev) can hide it cleanly
    // without rebuilding the frontend. See
    // argo-batch-pipeline/scripts/generate-myvalues.sh for where this is
    // wired into the deploy.
    const argoEnabled = window._env_.REACT_APP_ARGO_ENABLED === 'true';
    const { visible: adminVisible, label: adminLabel } = useAdminVisibility();

    const tabList = [
        {
            label: (<Typography variant='body1' sx={{ fontSize: { xs: 10, sm: 16 } }}>Catalog</Typography>),
            icon: <DatasetCatalogIcon fontSize={tabIconSize} />,
            component: <DatasetCatalog />,
            path: 'DatasetCatalog'
        },
        {
            label: (<Typography variant='body1' sx={{ fontSize: { xs: 10, sm: 16 } }}>Overview</Typography>),
            icon: <InspectorIcon fontSize={tabIconSize} />,
            component: <Inspector />,
            path: 'Inspector'
        },
        {
            label: (<Typography variant='body1' sx={{ fontSize: { xs: 10, sm: 16 } }}>Files</Typography>),
            icon: <FilesIcon fontSize={tabIconSize} />,
            component: <Files />,
            path: 'Files'
        },
        {
            label: (<Typography variant='body1' sx={{ fontSize: { xs: 10, sm: 16 } }}>Jupyterhub</Typography>),
            icon: <ApiIcon fontSize={tabIconSize} />,
            component: <JupyterLink />,
            path: 'JupyterLink'
        },
        ...(argoEnabled ? [{
            label: (<Typography variant='body1' sx={{ fontSize: { xs: 10, sm: 16 } }}>My Jobs</Typography>),
            icon: <WorkIcon fontSize={tabIconSize} />,
            component: <MyJobs />,
            path: 'MyJobs'
        }] : []),
        ...(adminVisible
            ? [{
                // Label comes from the visibility hook so admins see
                // "Admin" and read-only viewers see "My MQTT" — same
                // page component, different framing per the user's
                // relationship to the dataset list.
                label: (<Typography variant='body1' sx={{ fontSize: { xs: 10, sm: 16 } }}>{adminLabel}</Typography>),
                icon: <AdminPanelSettingsIcon fontSize={tabIconSize} />,
                component: <Admin />,
                path: 'Admin'
            }]
            : []),
        {
            label: (<Typography variant='body1' sx={{ fontSize: { xs: 10, sm: 16 } }}>About</Typography>),
            icon: <InfoIcon fontSize={tabIconSize} />,
            component: <About />,
            path: 'About'
        }
    ];

    // Compute the canonical tab index from the current URL. -1 if the
    // path is unknown (will trigger a redirect below).
    const indexFromUrl = tabList.findIndex(x => x.path === activeTab);

    // Mirror the URL into Redux any time it changes — including when the
    // tabList itself shrinks (e.g. Admin tab disappears because role was
    // revoked) so DatasetSelect and other readers don't get stuck on a
    // stale index. The previous useLayoutEffect ran only once on mount,
    // which is what F4 was about. (F3/F4)
    useEffect(() => {
        if (indexFromUrl < 0) {
            // Unknown path — redirect to the default tab. Don't touch
            // Redux yet; the redirect will trigger this effect again with
            // a valid index.
            navigate(`/tabselect/${DEFAULT_TAB_PATH}`, { replace: true });
            return;
        }
        if (value !== indexFromUrl) {
            dispatch(setValue(indexFromUrl));
        }
    }, [activeTab, tabList.length, indexFromUrl, value, dispatch, navigate]);

    const handleChange = (_event, newValue) => {
        if (newValue === indexFromUrl) return;
        navigate({
            pathname: `/tabselect/${tabList[newValue].path}`,
            search: searchParams.toString()
        });
    };

    // Render based on the URL-derived index. The Redux mirror is for
    // legacy reads only; this guarantees the UI never lags the URL.
    const renderIndex = indexFromUrl < 0 ? 0 : indexFromUrl;
    const tabsValue = indexFromUrl < 0 ? false : indexFromUrl;

    return (
        <div className={styles['tabs-header']}>
            <div className={styles['tabs-icon-menu']}>
                <Tabs
                    value={tabsValue}
                    to={tabsValue}
                    path={activeTab}
                    onChange={handleChange}
                    color="secondary"
                    aria-label="dataportal navigation tabs"
                    centered>
                    {tabList.map((o) =>
                        <Tab
                            key={o.path}
                            icon={o.icon}
                            label={o.label}
                            path={o.path}
                            sx={{
                                padding: { xs: '8px', sm: '16px' },
                                minWidth: '50px',
                                width: { xs: '64px', sm: '100px', lg: '150px' }
                            }}
                        />)}
                </Tabs>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DatasetSelect />
                </Box>
            </div>
            <div className={styles['tabs-body']}>
                <Background>
                    {tabList[renderIndex].component}
                </Background>
            </div>
        </div>
    );
}

export default TabSelect;
